/**
 * Where the merchant goes after signing in, and how a private page asks to be returned to.
 *
 * A signed-out merchant who opens a private page (the connected-apps allow page a partner
 * sent them to) is redirected to sign-in. The page they wanted travels in the URL as
 * `?redirect=`, never only in memory: the app may reload itself on the way (an expired
 * token makes the API client send the browser to /logout), and a browser refresh reloads
 * it too. In-memory state does not survive that; a URL does. ENG-4698.
 */

export const REDIRECT_PARAM = "redirect";

const START_PAGE = "/initial";

// Tabs and newlines are stripped by the URL parser before a path is resolved, so
// `/\t/evil.example` would resolve off-site; a control character has no place in a page.
const hasControlCharacter = (value: string): boolean =>
  Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code < 0x20 || code === 0x7f;
  });

// A base for the URL parser, only so the pathname comes back normalised (dot segments
// collapsed). Nothing is requested from it, and it plays no part in the origin check:
// a value that starts with one slash, with no backslash (which the parser treats as a
// slash) and no control character, cannot leave the portal's own origin, so a value
// that names this host, such as `//returnable.invalid/x`, is refused like any other.
const PARSER_BASE = "http://returnable.invalid";

/**
 * Only a same-origin page may be returned to, judged as the browser will resolve it: one
 * leading slash (never `//host`, `\\host` or a scheme), no control characters, and not a
 * sign-in or sign-out page once dot segments are collapsed, percent-encoding undone and
 * case ignored (routes match case-insensitively), which would loop.
 */
export function isReturnablePath(
  value: string | null | undefined
): value is string {
  if (
    !value ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    hasControlCharacter(value)
  ) {
    return false;
  }
  let resolved: URL;
  try {
    resolved = new URL(value, PARSER_BASE);
  } catch {
    return false;
  }
  let pathname: string;
  try {
    pathname = decodeURIComponent(resolved.pathname).toLowerCase();
  } catch {
    return false;
  }
  return !pathname.startsWith("/auth") && !pathname.startsWith("/logout");
}

/** The `redirect` a URL carries, when it can be returned to. */
export function requestedRedirect(search: string): string | undefined {
  const value = new URLSearchParams(search).get(REDIRECT_PARAM);
  return isReturnablePath(value) ? value : undefined;
}

// "Continue with Google" leaves the app for Google and comes back on
// /auth/complete-profile, which carries no `redirect`. The page travels in sessionStorage
// instead (same tab, like core/pendingInvite.ts). ENG-4705.
const GOOGLE_REDIRECT_KEY = "stream_google_sign_in_redirect";

/**
 * Before leaving for Google: keep the page the sign-in URL asks to return to, or forget
 * one an earlier attempt kept, so a stale page is never returned to.
 */
export function rememberGoogleRedirect(search: string): void {
  const page = requestedRedirect(search);
  if (page) {
    sessionStorage.setItem(GOOGLE_REDIRECT_KEY, page);
  } else {
    sessionStorage.removeItem(GOOGLE_REDIRECT_KEY);
  }
}

/** Back from Google: the page kept before leaving, when it can be returned to. */
export function rememberedGoogleRedirect(): string | undefined {
  const page = sessionStorage.getItem(GOOGLE_REDIRECT_KEY);
  return isReturnablePath(page) ? page : undefined;
}

export function forgetGoogleRedirect(): void {
  sessionStorage.removeItem(GOOGLE_REDIRECT_KEY);
}

/**
 * `<base>?redirect=<page>` for the page at `pathname + search`. When that page is itself
 * a sign-in or sign-out page, the `redirect` it already carries travels on instead (an
 * expired token can force a sign-out from /auth?redirect=… too). Bare base when there
 * is nothing to come back to.
 */
export function urlReturningTo(
  base: "/auth" | "/logout",
  pathname: string,
  search = ""
): string {
  const page = pathname + search;
  const target = isReturnablePath(page) ? page : requestedRedirect(search);
  return target
    ? `${base}?${REDIRECT_PARAM}=${encodeURIComponent(target)}`
    : base;
}

/**
 * The page to show once the merchant is signed in: the `redirect` the sign-in URL carries,
 * else the deep link the app remembered in memory, else the start page.
 */
export function postSignInDestination(
  search: string,
  deepLink?: string
): string {
  const requested = requestedRedirect(search);
  if (requested) {
    return requested;
  }
  if (isReturnablePath(deepLink)) {
    return deepLink;
  }
  return START_PAGE;
}
